import React, { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'

// Side-effect import: registers the --rv2-* CSS custom properties (colors,
// radii, spacing, fonts) this panel's stylesheets read, plus the Inter/IBM
// Plex Serif/JetBrains Mono font-faces. Shared with ReconciliationV2 so both
// surfaces render from the same design tokens.
import '../ReconciliationV2/tokens'

import StatCards from './StatCards'
import TransactionFilters from './TransactionFilters'
import TransactionsTable from './TransactionsTable'
import TransactionDetailModal from './TransactionDetailModal'
import TransactionEventDrawer from './TransactionEventDrawer'
import TicketDrawer from './TicketDrawer'
import { TD_Q, useTransactionDetails } from './hooks/useTransactionDetails'
import type {
  AllocationTicket,
  TransactionViewAllocation,
  TransactionViewRow,
} from '../../api/transaction-details-api-client'
import { buildTransactionsCsv, downloadCsv, DisplayRow } from './helpers'
import { replaceOwnedQuery } from '../ReconciliationV2/replaceOwnedQuery'
import { ADJ_LINK } from '../ReconciliationV2/BenefitsTable/helpers'
import AdjustPaymentModal, {
  AdjustPaymentTarget,
} from '../ReconciliationV2/AdjustPaymentModal'
import type { AdjustSectionKey } from '../ReconciliationV2/AdjustPaymentModal.types'
import { useConfirmUnreturnTransaction } from '../ReconciliationV2/useConfirmUnreturnTransaction'
import styles from './TransactionDetailsPanel.module.scss'

/**
 * Transaction Details ledger, rendered chrome-free so it can live inside a host
 * page's tab (Payment Reconciliation → "Transaction Details"). Owns its own
 * filter/pagination state and detail/ticket overlays.
 */
const TransactionDetailsPanel: React.FC = () => {
  const txn = useTransactionDetails()
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionViewRow | null>(null)
  const [selectedTickets, setSelectedTickets] = useState<
    AllocationTicket[] | null
  >(null)
  const [historyTxn, setHistoryTxn] = useState<TransactionViewRow | null>(null)
  const confirmUnreturn = useConfirmUnreturnTransaction()
  // Adjust Payment modal opened in place from a row's Action button (allocate
  // an unallocated txn, or edit an allocated one). Its mutations invalidate the
  // transaction-details queries (see RECON_V2_QUERY_KEYS), so the row's recon
  // status + the stat cards refresh on success without leaving this tab.
  const [adjustTarget, setAdjustTarget] = useState<AdjustPaymentTarget | null>(
    null
  )

  // Deep link in: when arriving via the Adjust Payment modal's "open in
  // Transaction Details" jump, the ledger is already filtered to that
  // transaction id (tdQ seeded the search). Once the row loads, auto-open its
  // detail modal matching the id in openTransactionId. deepLinkGen bumps on
  // every inbound jump (including the same id while this tab is already open)
  // so the effect re-runs. Wait for the search to settle so keepPreviousData
  // from the prior filter cannot satisfy/fail the match early. Clear tdOpen
  // afterward (same as BenefitsTable clears adj*). If the row never surfaces,
  // still clear and give up.
  const lastHandledDeepLinkGen = useRef<number | null>(null)
  useEffect(() => {
    const targetId = txn.openTransactionId
    if (!targetId) return
    if (lastHandledDeepLinkGen.current === txn.deepLinkGen) return
    // Filters from the deep link must be applied and the matching request done
    // before we look for the row (keepPreviousData would otherwise look like
    // a settled miss against the previous page).
    if (txn.appliedSearch !== targetId) return
    if (txn.isLoading || txn.isFetching) return
    if (!txn.data) return
    const match = txn.data.data.find((t) => t.transactionId === targetId)
    if (match) setSelectedTransaction(match)
    lastHandledDeepLinkGen.current = txn.deepLinkGen
    replaceOwnedQuery([TD_Q.open], {})
  }, [
    txn.openTransactionId,
    txn.deepLinkGen,
    txn.appliedSearch,
    txn.isLoading,
    txn.isFetching,
    txn.data,
  ])

  // Deep link out: jump to the Benefits tab and open the Adjust Payment modal
  // on this allocation's (benefit, coverage-month) cell. ReconciliationV2
  // listens to `tab=`; BenefitsTable reads adjBenefitId/adjPeriod on mount.
  const handleOpenAdjust = (
    allocation: TransactionViewAllocation,
    transactionId: string
  ) => {
    replaceOwnedQuery(
      ['tab', ADJ_LINK.benefitId, ADJ_LINK.period, ADJ_LINK.txn],
      {
        tab: 'benefits',
        [ADJ_LINK.benefitId]: allocation.benefitId,
        [ADJ_LINK.period]: allocation.periodStartDate,
        [ADJ_LINK.txn]: transactionId,
      }
    )
  }

  // Per-row Action button: open the Adjust Payment modal in place. An allocated
  // row (split parent → its first slice, child/plain → its own allocation)
  // opens in cell mode, preselecting this transaction so Apply rewrites its
  // whole split; an unallocated row opens in unassigned mode — the same flow
  // the Required Action tab uses (the modal self-resolves the benefit).
  // `initialSection` opens the modal on one accordion section — the Return
  // button lands on the Return note.
  const handleRowAction = (
    row: DisplayRow,
    initialSection?: AdjustSectionKey
  ) => {
    const { txn } = row
    const allocation = row.allocation ?? txn.allocations[0] ?? null
    if (allocation) {
      setAdjustTarget({
        benefitId: allocation.benefitId,
        periodStartDate: allocation.periodStartDate,
        benefitLabel: allocation.benefitLabel,
        benefitResolved: allocation.benefitResolved,
        employeePublicKey: allocation.employeePublicKey,
        insuredPersons: allocation.insuredPersons,
        carrierName: allocation.carrierName,
        preselectTransactionId: txn.transactionId,
      })
      return
    }
    setAdjustTarget({
      kind: 'unassigned',
      transactionId: txn.transactionId,
      transactionNumber: txn.transactionNumber,
      amount: txn.amount,
      carrier: txn.resolvedBenefit?.carrierName ?? null,
      transactionStatus: txn.transactionStatus,
      transactionType: txn.transactionType,
      // Seed the benefit picker with the resolved benefit so allocating a
      // known-benefit row is one step; the modal still self-resolves otherwise.
      prefillBenefit: txn.resolvedBenefit
        ? {
            benefitId: txn.resolvedBenefit.benefitId,
            label: txn.resolvedBenefit.benefitLabel,
          }
        : undefined,
      date: dayjs(txn.transactionDate).format('MMM D, YYYY'),
      conf: txn.token ?? txn.transactionId,
      source: txn.source,
      initialSection,
    })
  }

  // Server-side pagination means "Export" covers the currently loaded page
  // only; a full-dataset export would need to walk every page and is deferred.
  const handleExport = () => {
    const csv = buildTransactionsCsv(txn.displayRows)
    downloadCsv('transaction-details.csv', csv)
  }

  return (
    <>
      <div className={styles.page}>
        <StatCards
          reconciledCount={txn.summary?.reconciledCount ?? 0}
          pendingCount={txn.summary?.pendingCount ?? 0}
          totalAmount={txn.summary?.totalAmount ?? 0}
          absorbedAmount={txn.summary?.absorbedAmount ?? 0}
          returnedCount={txn.summary?.returnedCount ?? 0}
          returnedAmount={txn.summary?.returnedAmount ?? 0}
          loading={txn.isSummaryLoading}
          updating={txn.isSummaryFetching && !txn.isSummaryLoading}
        />

        <TransactionFilters
          search={txn.search}
          onSearchChange={txn.setSearch}
          onApplySearch={txn.applySearch}
          selectedSources={txn.selectedSources}
          onSourcesChange={txn.setSelectedSources}
          year={txn.year}
          onYearChange={txn.setYear}
          yearOptions={txn.yearOptions}
          monthFilter={txn.monthFilter}
          onMonthChange={txn.setMonthFilter}
          monthOptions={txn.monthOptions}
          clientFilter={txn.clientFilter}
          onClientChange={txn.setClientFilter}
          clientOptions={txn.clientOptions}
          reconStatuses={txn.reconStatuses}
          onReconStatusesChange={txn.setReconStatuses}
          splitOnly={txn.splitOnly}
          onSplitOnlyChange={txn.setSplitOnly}
          hasAbsorption={txn.hasAbsorption}
          onHasAbsorptionChange={txn.setHasAbsorption}
          noteTypes={txn.noteTypes}
          onNoteTypesChange={txn.setNoteTypes}
          noteSearch={txn.noteSearch}
          onNoteSearchChange={txn.setNoteSearch}
          onApplyNoteSearch={txn.applyNoteSearch}
          onReset={txn.reset}
          onExport={handleExport}
          exportDisabled={txn.displayRows.length === 0}
        />

        <TransactionsTable
          displayRows={txn.displayRows}
          searchQuery={txn.appliedSearch}
          isLoading={txn.isLoading}
          isFetching={txn.isFetching}
          total={txn.total}
          page={txn.page}
          limit={txn.limit}
          onPageChange={txn.setPage}
          onLimitChange={txn.setLimit}
          onRowClick={setSelectedTransaction}
          onOpenTickets={setSelectedTickets}
          onOpenAdjust={handleOpenAdjust}
          onRowAction={handleRowAction}
          onOpenHistory={setHistoryTxn}
          onReturn={(row) => handleRowAction(row, 'return')}
          onUnreturn={confirmUnreturn}
          noSourcesSelected={txn.noSourcesSelected}
          sortBy={txn.sortBy}
          sortOrder={txn.sortOrder}
          onToggleSort={txn.toggleSort}
        />
      </div>

      <TransactionDetailModal
        transaction={selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />

      <TicketDrawer
        open={!!selectedTickets}
        tickets={selectedTickets ?? []}
        onClose={() => setSelectedTickets(null)}
      />

      <TransactionEventDrawer
        open={!!historyTxn}
        transactionId={historyTxn?.transactionId ?? null}
        label={historyTxn?.transactionId ?? undefined}
        onClose={() => setHistoryTxn(null)}
      />

      <AdjustPaymentModal
        open={!!adjustTarget}
        target={adjustTarget}
        onClose={() => setAdjustTarget(null)}
      />
    </>
  )
}

export default TransactionDetailsPanel
